import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';

import { MIN_SEARCH_KEYWORD_LENGTH } from '../constants';
import { useLoadSuggestions } from '../hooks/useLoadSuggestions';
import { InputProps, Suggestion } from '../model';
import { getSearchDebounceTime } from '../utils';

type ProviderProps = {
    isSuggestionsVisible: boolean;
    onShowSuggestions: () => void;
    onHideSuggestions: () => void;
};

type ContextValue = {
    isLoading: boolean;
    isSuggestionsVisible: boolean;
    suggestions: Suggestion[];
    inputValue: string;
    setInputValue: (value: string) => void;
    focusedSuggestionIndex: number | null;
    setFocusedSuggestionIndex: (index: number | null) => void;
    selectedSuggestion: Suggestion;
    onSelect: (item: Suggestion | null) => void;
    onShowSuggestions: () => void;
    onHideSuggestions: () => void;
};

const InputContext = createContext<InputProps & ContextValue>(null);

export const InputContextProvider: FC<InputProps & ProviderProps & { children?: ReactNode }> = ({
    children,
    isSuggestionsVisible,
    onShowSuggestions,
    onHideSuggestions,
    onSelect: selectCallback,
    ...props
}) => {
    const [inputValue, setInputValue] = useState(props.value?.label || '');
    const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState<number>(null);
    const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion>(props.value || null);

    const isLoadEnabled = isSuggestionsVisible && !selectedSuggestion;
    const minKeywordLength = props.minKeywordLength ?? MIN_SEARCH_KEYWORD_LENGTH;
    const searchDebounceTime = props.searchDebounceTime ?? getSearchDebounceTime();
    const { isLoading, suggestions, submitKeyword } = useLoadSuggestions(
        props.loadSuggestions,
        inputValue,
        isLoadEnabled,
        minKeywordLength,
        searchDebounceTime,
    );

    const onSelect = (item: Suggestion | null) => {
        setSelectedSuggestion(item);
        selectCallback?.(item);
        if (item) {
            setInputValue(item?.label);
            onHideSuggestions();
        }
        if (item && !suggestions.find(({ value }) => value === item.value)) {
            submitKeyword(item.label);
        }
    };
    // Clear suggestion focus after loading new suggestions
    useEffect(() => {
        const selectedSuggestionIndex = suggestions.findIndex(({ value }) => value === selectedSuggestion?.value);
        setFocusedSuggestionIndex(selectedSuggestionIndex > 0 ? selectedSuggestionIndex : null);
    }, [isSuggestionsVisible, selectedSuggestion?.value, suggestions]);

    useEffect(() => {
        if (props.value) {
            setInputValue(props.value.label);
            if (props.shouldFetchOnValueChange) {
                submitKeyword(props.value.label);
            }
            setSelectedSuggestion(props.value);
        } else {
            setSelectedSuggestion(null);
        }
    }, [props.shouldFetchOnValueChange, props.value, submitKeyword]);

    return (
        <InputContext.Provider
            value={{
                ...props,
                onSelect,
                onShowSuggestions,
                onHideSuggestions,
                isSuggestionsVisible,
                isLoading,
                suggestions,
                inputValue,
                setInputValue,
                focusedSuggestionIndex,
                setFocusedSuggestionIndex,
                selectedSuggestion,
            }}
        >
            {children}
        </InputContext.Provider>
    );
};

export const useInputContext = () => {
    const value = useContext(InputContext);
    if (!value) {
        throw new Error('useInputContext must be used within InputContextProvider');
    }

    return value;
};
